import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { ApiError } from "../client/index.ts";
import type { CourseDetail, CourseSummary, LessonView, Progress } from "../schema/index.ts";
import { useSession } from "./auth.ts";
import { useCourseKit } from "./provider.tsx";
import { queryKeys } from "./query-keys.ts";

export const isNotFound = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 404;

export const useCourses = (): UseQueryResult<CourseSummary[]> => {
  const { api } = useCourseKit();
  return useQuery({ queryKey: queryKeys.courses(), queryFn: () => api.listCourses() });
};

export const useCourse = (slug: string): UseQueryResult<CourseDetail> => {
  const { api } = useCourseKit();
  return useQuery({ queryKey: queryKeys.course(slug), queryFn: () => api.getCourse(slug) });
};

export const useLessonView = (
  courseSlug: string,
  lessonSlug: string,
): UseQueryResult<LessonView> => {
  const { api } = useCourseKit();
  return useQuery({
    queryKey: queryKeys.lesson(courseSlug, lessonSlug),
    queryFn: () => api.getLesson(courseSlug, lessonSlug),
  });
};

/** Completed lesson ids of the signed-in learner; empty for visitors. */
export const useCompletedLessons = (
  courseSlug: string,
): { completed: ReadonlySet<string>; ready: boolean } => {
  const { api } = useCourseKit();
  const { user } = useSession();
  const query = useQuery({
    queryKey: queryKeys.progress(courseSlug),
    queryFn: () => api.getProgress(courseSlug),
    enabled: user !== null,
    select: (progress) => new Set(progress.completedLessonIds),
  });
  return { completed: query.data ?? new Set<string>(), ready: user !== null && query.isSuccess };
};

export const useCompleteLesson = (
  courseSlug: string,
): UseMutationResult<Progress, Error, string> => {
  const { api } = useCourseKit();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lessonId: string) => api.setLessonProgress(lessonId, { completed: true }),
    onSuccess: (progress) => queryClient.setQueryData(queryKeys.progress(courseSlug), progress),
  });
};
